import { Box, Button, Drawer, drawerClasses, styled } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import type { AddToCartResponseItem } from "~/features/cart/cart/api/AddToCart";
import { LinkButton } from "~/common/components/Link";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import type { ProductData } from "~/domains/product/types/ProductData";
import type { RootState } from "~/app/store";
import { selectIsInFlight } from "~/features/search/redux/searchSelectors";
import { useMultipleRelatedProducts } from "~/domains/product/redux/relatedProducts/useRelatedProducts";
import { resolveCartPageUrl } from "~/utils/url/urlUtils";
import { useMultipleTopsellerProducts } from "~/domains/product/redux/topsellerProducts/useTopsellerProducts";
import { Recommendations } from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/components/CartSuccessRecommendations/Recommendations";
import type { PurchasePlace } from "~/app/analytics/ga/recommendations/types";
import { RecommendationsSkeleton } from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/components/CartSuccessRecommendations/RecommendationsSkeleton";
import AddToCartResult from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/components/AddToCartResult";
import { getProductDataForResponseItem } from "~/features/cart/cart/utils/addToCartResponseItems";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { usePriceReferenceSelection } from "~/app/config/useSession";

interface CartSuccessModalProps {
  onClose: () => void;
  linearConfig?: ProductData;
  className?: string;
  page: PurchasePlace;
  responseItems: AddToCartResponseItem[];
}
const CartSuccessModal = ({ onClose, linearConfig, className, page, responseItems }: CartSuccessModalProps) => {
  const { t } = useTranslation();
  const { selection } = usePriceReferenceSelection();
  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMinTabletSize();

  const inFlight = useSelector(selectIsInFlight);

  if (inFlight) {
    onClose();
  }

  const resultProductCodes = responseItems.map((entry) => entry.productCode);
  const productCode = resultProductCodes[0] ?? "";

  const productDescriptor = ProductService.useProductDescriptor({
    productCode: productCode,
    priceReference: getInitialPriceReference(selection),
    options: { fetch: true },
  });

  //we only need product info if it's for single entries

  const maxFetchedRelatedProducts = 5;

  const getProductBasicInfo = (state: RootState) => state?.products.basic[productCode];

  const productBasicInfoFromStore = useSelector(getProductBasicInfo);
  const productBasicInfoFromResponse = responseItems[0] ? getProductDataForResponseItem(responseItems[0]) : undefined;
  const productBasicInfo = linearConfig ?? productBasicInfoFromStore ?? productBasicInfoFromResponse;

  const { relatedProducts, state: relatedState } = useMultipleRelatedProducts(
    resultProductCodes.slice(0, maxFetchedRelatedProducts),
  );

  const { topsellerProducts, state: topsellerState } = useMultipleTopsellerProducts(
    resultProductCodes.slice(0, maxFetchedRelatedProducts),
  );

  // We only show related products on the CartSuccessModal if the product's line is whitelisted
  const visibleRelatedProducts = relatedProducts.filter((product) => product.showPostCartModal);

  let recommendations;
  if (relatedState === "loading" || topsellerState === "loading") {
    recommendations = (
      <>
        <RecommendationsSkeleton title={t("addToCart.relatedProducts")} />
        <RecommendationsSkeleton title={t("addToCart.topsellerProducts")} />
      </>
    );
  } else {
    recommendations = (
      <>
        <Recommendations
          entries={visibleRelatedProducts}
          title={t("addToCart.relatedProducts")}
          tracking-aux-reco-type="rsp"
          place={page}
          tracking-aux-title={t("addToCart.relatedProducts", { lng: "en" })}
          tracking-aux-location="add-to-cart-pop-up"
        />
        <Recommendations
          entries={topsellerProducts}
          title={t("addToCart.topsellerProducts")}
          tracking-aux-reco-type="top"
          place={page}
          tracking-aux-title={t("addToCart.topsellerProducts", { lng: "en" })}
          tracking-aux-location="add-to-cart-pop-up"
        />
      </>
    );
  }

  return (
    <StyledDrawer
      anchor={isDesktop ? "right" : "bottom"}
      className={className}
      sx={{
        "& .MuiAlert-standard": {
          width: "100%",
          alignSelf: "center",
        },
      }}
      open
      onClose={onClose}
    >
      <CloseBar>
        <LinkButton type="button" onClick={onClose}>
          <CloseIcon />
        </LinkButton>
      </CloseBar>
      <ModalBody>
        <DialogContent>
          <AddToCartResult productInfo={productBasicInfo} responseItems={responseItems} />
          {!isTablet && <MobileRecommendations>{recommendations}</MobileRecommendations>}
        </DialogContent>
      </ModalBody>
      {productDescriptor.base && (
        <ActionsContainer>
          <Button
            color={isTablet ? "secondary" : "primary"}
            variant={isTablet ? "contained" : "text"}
            fullWidth
            onClick={onClose}
            sx={{ gridArea: "continue" }}
          >
            {t("notFound.continue")}
          </Button>
          <Button
            href={resolveCartPageUrl()}
            startIcon={<ShoppingCartIcon />}
            fullWidth
            sx={{ gridArea: "cart", height: { tablet: "min-content" } }}
          >
            {t("addToCartPopup.checkoutBtn")}
          </Button>
        </ActionsContainer>
      )}
      {isTablet && <TabletRecommendations>{recommendations}</TabletRecommendations>}
    </StyledDrawer>
  );
};

export default CartSuccessModal;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`.${drawerClasses.paper}`]: {
    width: "100wv",
    height: "100dvh",
    [theme.breakpoints.up("desktop-s")]: {
      width: "472px",
    },
    [theme.breakpoints.up("desktop-m")]: {
      width: "528px",
    },
    [theme.breakpoints.up("desktop-l")]: {
      width: "672px",
    },
  },
}));

const CloseBar = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "end",
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up("tablet")]: {
    borderBottom: "none",
  },
}));

const ModalBody = styled(Box)(({ theme }) => ({
  overflowY: "visible",
  padding: 0,
  [theme.breakpoints.up("tablet")]: {
    flex: 0,
  },
}));

const DialogContent = styled("div")(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(2),
  display: "flex",
  gap: theme.spacing(2),
  flexDirection: "column",
  [theme.breakpoints.up("tablet")]: {
    padding: theme.spacing(0, 3),
  },
}));

const MobileRecommendations = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(10),
}));

const ActionsContainer = styled(Box)(({ theme }) => ({
  zIndex: 10,
  background: theme.palette.background.paper,
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  display: "grid",
  gridTemplateColumns: "1fr 2fr 2fr 1fr",
  columnGap: theme.spacing(1),
  gridTemplateAreas: `"continue continue cart cart"`,
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up("tablet")]: {
    position: "static",
    columnGap: theme.spacing(3),
    gridTemplateAreas: `". continue cart ."`,
    padding: theme.spacing(2, 3, 0),
    borderTop: "none",
  },
  [theme.breakpoints.up("desktop-s")]: {
    columnGap: theme.spacing(2),
    gridTemplateAreas: `"continue continue cart cart"`,
  },
}));

const TabletRecommendations = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up("desktop-s")]: {
    padding: theme.spacing(0, 3),
  },
}));
